/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2020 - 2026
///                                    RXO Inc.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of RXO Inc. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { difference } from "lodash";

import SelectModesModal from "./SelectModesModal";
import AdditionalModesWarningModal from "./AdditionalModesWarningModal";
import AlertModalForSalesRep from "./AlertModalForSalesRep";
import DocumentUploadModal from "../summary/DocumentUploadModal";
import SelfPricedClosedWonDetailsModal from "./SelfPricedClosedWonDetailsModal";
import {
  setModeStatusForAwardUploaded,
  setModeStatusForSelfPricedAwardsUploaded
} from "../../../api/bids";
import {
  fetchBidSummary,
  fetchBidMessages,
  removeDocumentToUpload,
  fetchBidAttachments
} from "../../../slices/bidSummarySlice";
import { fetchBidDetails } from "../../../slices/bidDetailsSlice";
import { status as modeStatus, repType, mode } from "../../../helpers/types";
import messages from "../../../helpers/messages";
import { isEffectiveCoyoteBid } from "../../../helpers/isEffectiveCoyoteBid";

function AwardUploadModal({
  open,
  summary,
  user,
  handleModalClose,
  selfPricedCloseWon = false
}) {
  const dispatch = useDispatch();
  const route = useLocation().pathname.split("/")[3] || "";
  const { bidId } = summary;
  const { role } = user;
  const modes = summary.modes.filter(x => x.status !== modeStatus.closed);

  const useSelfPricedCloseWonFlow = Boolean(
    selfPricedCloseWon &&
      !isEffectiveCoyoteBid(summary) &&
      modes.some(
        m => m.bidOwnerRepType === repType.sales && m.mode === mode.tl
      )
  );

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [winningModes, setWinningModes] = useState([]);
  const [openDocumentUploadModal, setDocumentUploadModal] = useState(false);
  const [openAdditionalModesWarningModal, setAdditionalModesWarningModal] =
    useState(false);
  const [showAlertModalForSalesRep, setShowAlertModalForSalesRep] =
    useState(false);
  const [showClosedWonDetailsStep, setShowClosedWonDetailsStep] =
    useState(false);
  const [selfPricedAwardDocsUploaded, setSelfPricedAwardDocsUploaded] =
    useState(false);

  const lastSelfPricedAwardsBodyRef = useRef(null);

  const refreshAfterAwardAction = () => {
    dispatch(fetchBidSummary({ bidId, role }));
    route === "summary" && dispatch(fetchBidMessages(bidId));
    route === "details" && dispatch(fetchBidDetails(bidId));
  };

  const submitSelfPricedAwardsToApi = () => {
    const body = lastSelfPricedAwardsBodyRef.current;
    if (!body) {
      return;
    }
    setIsLoading(true);
    setIsError(false);
    setModeStatusForSelfPricedAwardsUploaded(bidId, body)
      .then(() => {
        const { awardedModes } = body;
        setIsLoading(false);
        setIsSuccess(true);
        setShowClosedWonDetailsStep(false);
        if (
          modes.some(x => x.bidOwnerRepType === repType.sales) &&
          awardedModes.some(x => x === mode.tl)
        ) {
          setShowAlertModalForSalesRep(true);
        } else {
          refreshAfterAwardAction();
          handleModalClose();
        }
      })
      .catch(() => {
        setIsError(true);
        setIsLoading(false);
      });
  };

  const handleSelfPricedDetailsFormSubmit = ({
    closedWonReason,
    winLossComments
  }) => {
    const awardedModes =
      modes.length > 1 ? winningModes.map(w => w.mode) : [modes[0].mode];
    lastSelfPricedAwardsBodyRef.current = {
      awardedModes,
      closedWonReason,
      winLossComments
    };
    submitSelfPricedAwardsToApi();
  };

  const handleModeStatusForAwardUploaded = () => {
    setIsLoading(true);
    setIsError(false);
    const awardedModes =
      modes.length > 1 ? winningModes.map(w => w.mode) : [modes[0].mode];
    setModeStatusForAwardUploaded(bidId, awardedModes)
      .then(() => {
        setIsSuccess(true);
        setIsLoading(false);
        if (
          modes.some(x => x.bidOwnerRepType === repType.sales) &&
          awardedModes.some(x => x === mode.tl)
        ) {
          setShowAlertModalForSalesRep(true);
        }
      })
      .catch(() => {
        setIsError(true);
        setIsLoading(false);
      });
  };

  const handleSelfPricedStep1UploadComplete = () => {
    dispatch(removeDocumentToUpload({ removeAll: true }));
    dispatch(fetchBidAttachments(bidId));
    setDocumentUploadModal(false);
    setSelfPricedAwardDocsUploaded(true);
    setShowClosedWonDetailsStep(true);
  };

  const handleSelfPricedDetailsCancel = () => {
    setIsError(false);
    setIsLoading(false);
    setShowClosedWonDetailsStep(false);
    lastSelfPricedAwardsBodyRef.current = null;
    handleModalClose();
  };

  const handleNextButton = winningModesArg => {
    const losingModes = difference(modes, winningModesArg);
    if (losingModes.length) {
      setAdditionalModesWarningModal(true);
    } else {
      setDocumentUploadModal(true);
    }
  };

  const handleContinueButton = () => {
    setAdditionalModesWarningModal(false);
    setDocumentUploadModal(true);
  };

  const closeSelectModesModal = () => {
    setWinningModes([]);
    handleModalClose();
  };

  const closeAdditionalModesWarningModal = () => {
    setWinningModes([]);
    setAdditionalModesWarningModal(false);
    handleModalClose();
  };

  const closeDocumentUploadModal = submitted => {
    setIsError(false);
    setIsSuccess(false);
    setShowAlertModalForSalesRep(false);
    if (submitted) {
      refreshAfterAwardAction();
    }
    handleModalClose();
  };

  const selectModesOpen =
    open &&
    modes.length !== 1 &&
    !openAdditionalModesWarningModal &&
    !openDocumentUploadModal &&
    !(useSelfPricedCloseWonFlow && selfPricedAwardDocsUploaded) &&
    !showClosedWonDetailsStep;

  const showDocumentUpload =
    open &&
    !showClosedWonDetailsStep &&
    !selfPricedAwardDocsUploaded &&
    (openDocumentUploadModal || modes.length === 1);

  return (
    <>
      <SelectModesModal
        open={selectModesOpen}
        modes={modes}
        winningModes={winningModes}
        handleClose={closeSelectModesModal}
        handleNextButton={handleNextButton}
        setWinningModes={nextModes => setWinningModes(nextModes)}
      />
      <AdditionalModesWarningModal
        open={openAdditionalModesWarningModal}
        modes={modes}
        winningModes={winningModes}
        handleClose={closeAdditionalModesWarningModal}
        handleBackButton={() => setAdditionalModesWarningModal(false)}
        handleContinueButton={handleContinueButton}
      />
      {showDocumentUpload && (
        <DocumentUploadModal
          bidId={bidId}
          handleClose={closeDocumentUploadModal}
          modalTitle={
            useSelfPricedCloseWonFlow
              ? messages.selfPricedAwardUploadStep1Title.defaultMessage
              : messages.awardUpload.defaultMessage
          }
          modalContent={messages.uploadTheShipperAwardDocument.defaultMessage}
          checkboxContent={
            useSelfPricedCloseWonFlow
              ? undefined
              : messages.awardUploadModalCheckboxMessage.defaultMessage
          }
          onSuccess={
            useSelfPricedCloseWonFlow
              ? handleSelfPricedStep1UploadComplete
              : handleModeStatusForAwardUploaded
          }
          isLoading={isLoading}
          isSuccess={useSelfPricedCloseWonFlow ? true : isSuccess}
          isError={isError}
          alertTitle={messages.uploadComplete.defaultMessage}
          alertMessage={
            messages.awardUploadForNonBidOwnerSuccessMessage.defaultMessage
          }
          alertModalLarge={true}
          showAlertModal={!showAlertModalForSalesRep && !useSelfPricedCloseWonFlow}
          isAwardUpload={true}
        />
      )}
      {useSelfPricedCloseWonFlow && showClosedWonDetailsStep && (
        <SelfPricedClosedWonDetailsModal
          open={open}
          isLoading={isLoading}
          isError={isError}
          onSubmit={handleSelfPricedDetailsFormSubmit}
          onCancel={handleSelfPricedDetailsCancel}
          onRetrySubmit={submitSelfPricedAwardsToApi}
        />
      )}
      {showAlertModalForSalesRep && (
        <AlertModalForSalesRep
          open={showAlertModalForSalesRep}
          handleClose={closeDocumentUploadModal}
          bidId={bidId}
          user={user}
        />
      )}
    </>
  );
}

export default AwardUploadModal;
